import { apiClient as api } from './api';

/**
 * Enhanced service for document management with versioning and tagging
 */

/**
 * Save a document with version control
 * @param {string} content - Markdown content of the document
 * @param {string} documentName - Name of the document
 * @param {Object} metadata - Document metadata
 * @param {string} [documentId] - Optional document ID for updating existing document
 * @param {boolean} [isNewDocument=false] - Flag to indicate if this should be treated as a new document
 * @param {number} [projectId] - Optional project ID to associate with the document
 * @param {string} [documentType='functional_spec'] - Document type
 * @returns {Promise} - The API response
 */
export const saveMarkdown = async (
  content, 
  documentName, 
  metadata = {}, 
  documentId = null, 
  isNewDocument = false,
  projectId = null,
  documentType = 'functional_spec'
) => {
  try {
    const response = await api.post('/api/save-markdown', {
      markdown: content,
      document_name: documentName,
      metadata,
      document_id: documentId,
      is_new_document: isNewDocument,
      project_id: projectId,
      document_type: documentType
    });
    return response.data;
  } catch (error) {
    console.error(`Error saving markdown: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Download a document in the specified format
 * @param {string} content - Markdown content to convert
 * @param {string} format - Format to convert to (pdf or docx)
 * @param {Object} parentInfo - Information about the parent document
 * @param {string} [documentId] - Optional document ID to use for consistent naming
 * @returns {Promise} - The API response
 */
export const downloadDocument = async (content, format, parentInfo = {}, documentId = null) => {
  try {
    const endpoint = format === 'pdf' ? '/convert-to-pdf' : '/convert-to-docx';
    
    const requestBody = {
      markdown: content,
      company_info: parentInfo,
      save_to_gcs: true,
      document_name: parentInfo.document_name || `SAP Functional Spec ${new Date().toISOString().split('T')[0]}`,
      document_id: documentId
    };
    
    if (parentInfo.project_id) {
      requestBody.company_info.project_id = parentInfo.project_id;
    }
    
    const response = await api.post(endpoint, requestBody, {
      responseType: 'blob',
      headers: documentId ? {
        'X-Document-ID': documentId
      } : {}
    });
    
    // Check if the response contains a document ID in headers
    const responseDocumentId = response.headers['x-document-id'];
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `SAP_Functional_Spec.${format}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    return {
      success: true,
      document_id: responseDocumentId || documentId
    };
  } catch (error) {
    console.error(`Error downloading document: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Create and store a document in the specified format without downloading
 * @param {string} content - Markdown content to convert
 * @param {string} format - Format to convert to (pdf or docx)
 * @param {Object} parentInfo - Information about the parent document
 * @param {string} [documentId] - Optional document ID to use for consistent naming
 * @returns {Promise} - The API response
 */
export const createDocument = async (content, format, parentInfo = {}, documentId = null) => {
  try {
    const endpoint = format === 'pdf' ? '/convert-to-pdf' : '/convert-to-docx';
    
    const requestBody = {
      markdown: content,
      company_info: parentInfo,
      save_to_gcs: true,
      store_only: true,  // Only store, don't download
      document_name: parentInfo.document_name || `SAP Functional Spec ${new Date().toISOString().split('T')[0]}`,
      document_id: documentId
    };
    
    if (parentInfo.project_id) {
      requestBody.company_info.project_id = parentInfo.project_id;
    }
    
    // Don't set responseType to 'blob' since we don't want to download
    const response = await api.post(endpoint, requestBody, {
      headers: documentId ? {
        'X-Document-ID': documentId
      } : {}
    });
    
    // The response should be JSON when store_only is true
    return response.data;
  } catch (error) {
    console.error(`Error creating ${format} document:`, error);
    console.error(`Error details:`, {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data
    });
    return {
      success: false,
      error: error.response?.data?.error || error.response?.data?.message || error.message || `Failed to create ${format} document`
    };
  }
};

/**
 * Download a document by its ID
 * @param {string} documentId - The document ID
 * @param {string} [format] - Optional format (will use the document's stored format if not specified)
 * @param {boolean} [isSolutionProposal=false] - Whether this is a solution proposal document
 * @param {string} [projectId] - Project ID (required for solution proposal documents)
 * @returns {Promise} - The API response
 */
export const downloadDocumentById = async (documentId, format = null, isSolutionProposal = false, projectId = null) => {
  try {
    if (!documentId && !isSolutionProposal) {
      console.error('No document ID provided for download');
      return false;
    }
    
    if (isSolutionProposal && !projectId) {
      console.error('Project ID is required for solution proposal documents');
      return false;
    }
    
    // Determine the URL based on document type
    let url;
    if (isSolutionProposal) {
      // For solution proposal documents, use the special endpoint
      url = `/projects/${projectId}/solution-proposal/export/${format || 'docx'}/download`;
      console.log(`Downloading solution proposal document for project: ${projectId}, Format: ${format || 'docx'}`);
    } else {
      // For regular documents, use the standard endpoint
      url = `/get-document/${documentId}?download=true`;
      if (format) {
        url += `&format=${format}`;
      }
      console.log(`Downloading document: ${documentId}, Format: ${format || 'default'}`);
      console.log(`Document ID: ${documentId}`);
      console.log(`Requested Format: ${format || 'default'}`);
    }
    
    // Use direct fetch instead of apiClient to have more control over URL
    const BACKEND_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? window.location.protocol + '//' + window.location.hostname + ':5001/api'
      : window.location.protocol + '//' + window.location.hostname + '/api';
    
    const fullUrl = `${BACKEND_URL}${url}`;
    console.log(`Making request to: ${fullUrl}`);
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/octet-stream, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      console.error(`Download failed: HTTP ${response.status} ${response.statusText}`);
      return false;
    }
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'document';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    // If no filename was provided, create a descriptive one
    if (filename === 'document') {
      let docTypePrefix = 'SAP_Functional_Spec';
      if (isSolutionProposal) {
        docTypePrefix = 'Solution_Proposal';
      }
      
      const timestamp = new Date().toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d+/g, '')
        .replace('T', '_')
        .substring(0, 15);
        
      filename = isSolutionProposal 
        ? `${docTypePrefix}_Project_${projectId}_${timestamp}` 
        : `${docTypePrefix}_${documentId}_${timestamp}`;
      
      // Add extension based on requested format
      if (format) {
        filename += `.${format}`;
      } else if (isSolutionProposal) {
        filename += '.docx'; // Default for solution proposals
      }
    }
    
    // Get the blob from the response
    const blob = await response.blob();
    
    // Create blob URL and trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
    
    return true;
  } catch (error) {
    console.error(`Error downloading document by ID: ${error.message}`);
    return false;
  }
};

/**
 * Get all versions of a document
 * @param {string} documentId - The document ID
 * @returns {Promise} - The API response
 */
export const getDocumentVersions = async (documentId) => {
  try {
    const response = await api.get(`/document-versions/${documentId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching document versions: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Get document content
 * @param {string} documentId - The document ID
 * @param {string} [version] - Optional specific version
 * @returns {Promise} - The API response
 */
export const getDocumentContent = async (documentId, version = null) => {
    try {
      let url = `/document-content/${documentId}`;
      const params = new URLSearchParams();
      
      if (version) {
        params.append('version', version);
      }
      // Always request markdown format
      params.append('format', 'md');
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url, { 
        responseType: 'text',
        headers: {
          'Accept': 'text/markdown, text/plain'
        }
      });
      
      // Check if the response might be binary data
      if (typeof response.data === 'string') {
        // Check if this looks like a binary file
        if (response.data.startsWith('PK') || 
            response.data.includes('\u0000') ||
            /^\uFEFF/.test(response.data)) {
          
          return {
            success: false,
            error: 'Document is in binary format and cannot be displayed as text',
            content: '# Binary Document\n\nThis document is in a binary format and cannot be displayed. Please download the original document.'
          };
        }
        
        return {
          success: true,
          content: response.data
        };
      } else {
        return {
          success: false,
          error: 'Unexpected response format',
          content: '# Error\n\nUnexpected document format received.'
        };
      }
    } catch (error) {
      console.error(`Error fetching document content: ${error.message}`);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        content: `# Error\n\nFailed to load document: ${error.message}`
      };
    }
  };

/**
 * Add tags to a document
 * @param {string} documentId - The document ID
 * @param {Array<string|Object>} tags - Array of tag names or tag objects with {name, color}
 * @returns {Promise} - The API response
 */
export const addDocumentTags = async (documentId, tags) => {
  try {
    const response = await api.post(`/documents/${documentId}/tags`, { tags });
    return response.data;
  } catch (error) {
    console.error(`Error adding document tags: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Remove a tag from a document
 * @param {string} documentId - The document ID
 * @param {string} tagName - The tag name to remove
 * @returns {Promise} - The API response
 */
export const removeDocumentTag = async (documentId, tagName) => {
  try {
    const response = await api.delete(`/documents/${documentId}/tags/${encodeURIComponent(tagName)}`);
    return response.data;
  } catch (error) {
    console.error(`Error removing document tag: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};

/**
 * Delete multiple documents
 * @param {Array<string>} documentIds - Array of document IDs to delete
 * @param {boolean} [deleteAllVersions=false] - Whether to delete all versions of each document
 * @returns {Promise} - The API response
 */
export const deleteMultipleDocuments = async (documentIds, deleteAllVersions = false) => {
  try {
    const response = await api.post('/delete-documents', {
      document_ids: documentIds,
      all_versions: deleteAllVersions
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting documents: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};